// context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile,
    GoogleAuthProvider, // <-- NEW
    signInWithPopup     // <-- NEW
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../services/firebase";

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    const signup = async (email, password, displayName, role = 'customer') => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        if (displayName) {
            await updateProfile(userCredential.user, {
                displayName: displayName
            });
        }

        await setDoc(doc(db, "users", userCredential.user.uid), {
            email: email,
            displayName: displayName,
            role: role,
            createdAt: new Date(),
            isActive: true
        });

        return userCredential;
    };

    const login = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    // --- NEW: Google Sign In ---
    const googleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        const user = result.user;

        // Check if this is a first-time Google login. If so, create their Firestore profile.
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) {
            await setDoc(userDocRef, {
                email: user.email,
                displayName: user.displayName || '',
                role: 'customer',
                createdAt: new Date(),
                isActive: true
            });
        }
        return result;
    };

    const logout = () => {
        setUserRole(null);
        return signOut(auth);
    };

    const isAdmin = () => {
        return userRole === 'admin';
    };

    const isCustomer = () => {
        return userRole === 'customer';
    };

    const getUserRole = async (user) => {
        if (!user) return null;

        try {
            const userDoc = await getDoc(doc(db, "users", user.uid));
            if (userDoc.exists()) {
                return userDoc.data().role || 'customer';
            }
            await setDoc(doc(db, "users", user.uid), {
                email: user.email,
                displayName: user.displayName || '',
                role: 'customer',
                createdAt: new Date(),
                isActive: true
            });
            return 'customer';
        } catch (error) {
            console.error("Error getting user role:", error);
            return 'customer';
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const role = await getUserRole(user);
                setUserRole(role);
                setCurrentUser(user);
            } else {
                setCurrentUser(null);
                setUserRole(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        userRole,
        signup,
        login,
        googleSignIn, // <-- NEW
        logout,
        isAdmin,
        isCustomer
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export default AuthContext;